import { useEffect, useRef } from 'react';
const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');
function isFocusable(element) {
    if (element.hasAttribute('disabled'))
        return false;
    if (element.getAttribute('aria-hidden') === 'true')
        return false;
    if (element.closest('[aria-hidden="true"], [inert]'))
        return false;
    return true;
}
function getFocusableElements(container) {
    return Array.from(container.querySelectorAll(focusableSelector)).filter(isFocusable);
}
function getModalRoot(modal) {
    return modal.closest('[data-sam-modal-root]') ?? modal;
}
function hideBackgroundSiblings(modal) {
    const modalRoot = getModalRoot(modal);
    const siblings = Array.from(document.body.children).filter((element) => element instanceof HTMLElement && element !== modalRoot);
    return siblings.map((element) => {
        const state = {
            element,
            ariaHidden: element.getAttribute('aria-hidden'),
            inert: element.inert,
        };
        element.setAttribute('aria-hidden', 'true');
        element.inert = true;
        return state;
    });
}
function restoreBackgroundSiblings(states) {
    states.forEach(({ element, ariaHidden, inert }) => {
        if (ariaHidden === null) {
            element.removeAttribute('aria-hidden');
        }
        else {
            element.setAttribute('aria-hidden', ariaHidden);
        }
        element.inert = inert;
    });
}
export function useModalInteraction({ enabled, modalRef, onEscape, restoreFocus = true, lockScroll = true, isolateBackground = true, }) {
    const previouslyFocusedElementRef = useRef(null);
    /**
     * `onEscape` is read through a ref so it stays OUT of the effect's dep array.
     *
     * Almost every caller passes an inline arrow (`onClose={() => setOpen(false)}`),
     * which is a new function identity on each of the parent's renders. With
     * `onEscape` as a dependency, an ordinary parent re-render — a ProjectData
     * WebSocket session event, a session-sync poll tick — tore this entire effect
     * down and set it back up. Teardown calls `previouslyFocused.focus()` and setup
     * calls `modal.focus()`, so the field the user was typing into got blurred every
     * time. On a phone a blur closes the software keyboard, which made the agent
     * profile dialog unusable. The effect models the modal's open/close lifecycle,
     * so it must depend on `enabled` — not on callback identity.
     *
     * See `.claude/rules/64-unstable-prop-identity-remounts-subtrees.md`.
     */
    const onEscapeRef = useRef(onEscape);
    useEffect(() => {
        onEscapeRef.current = onEscape;
    }, [onEscape]);
    useEffect(() => {
        if (!enabled)
            return;
        const modal = modalRef.current;
        const previousBodyOverflow = document.body.style.overflow;
        const hiddenSiblingStates = modal && isolateBackground ? hideBackgroundSiblings(modal) : [];
        previouslyFocusedElementRef.current = document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
        if (lockScroll) {
            document.body.style.overflow = 'hidden';
        }
        modal?.focus();
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onEscapeRef.current?.();
                return;
            }
            if (event.key !== 'Tab')
                return;
            const currentModal = modalRef.current;
            if (!currentModal)
                return;
            const focusable = getFocusableElements(currentModal);
            if (focusable.length === 0) {
                event.preventDefault();
                currentModal.focus();
                return;
            }
            const first = focusable[0];
            const last = focusable.at(-1);
            if (!first || !last)
                return;
            const active = document.activeElement;
            const activeIsInside = active instanceof Node && currentModal.contains(active);
            if (event.shiftKey && (active === first || active === currentModal || !activeIsInside)) {
                event.preventDefault();
                last.focus();
                return;
            }
            if (!event.shiftKey && (active === last || active === currentModal || !activeIsInside)) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            if (lockScroll) {
                document.body.style.overflow = previousBodyOverflow;
            }
            restoreBackgroundSiblings(hiddenSiblingStates);
            const previouslyFocused = previouslyFocusedElementRef.current;
            if (restoreFocus && previouslyFocused?.isConnected) {
                previouslyFocused.focus();
            }
            previouslyFocusedElementRef.current = null;
        };
    }, [enabled, isolateBackground, lockScroll, modalRef, restoreFocus]);
}
//# sourceMappingURL=useModalInteraction.js.map